import React from 'react';
import { Calculator, TreePine, Car, Home, Plane, Droplet, Zap, Info, ArrowRight, Leaf } from 'lucide-react';

export default function ImpactCalculator({ impactTonnes, setImpactTonnes, setRetirementAmount }) {
    const tonnes = parseInt(impactTonnes) || 0;

    const calculations = {
        trees: tonnes * 50,
        car_km: tonnes * 4000,
        home_months: tonnes * 1.2,
        flights: tonnes / 0.5,
        gallons_gas: tonnes * 110,
        smartphones: tonnes * 120000
    };

    return (
        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 md:p-8 border border-blue-100 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-blue-600 rounded-xl">
                    <Calculator className="text-white" size={24} />
                </div>
                <div>
                    <h3 className="text-lg font-black text-gray-900 uppercase tracking-tight">Carbon Impact Calculator</h3>
                    <p className="text-xs text-gray-600 font-medium">Understand your environmental impact</p>
                </div>
            </div>

            {/* Input Section */}
            <div className="mb-8">
                <label className="block text-sm font-bold text-gray-700 mb-3">
                    Number of Tonnes to Offset
                </label>
                <div className="relative">
                    <input
                        type="number"
                        value={impactTonnes}
                        onChange={(e) => setImpactTonnes(e.target.value)}
                        className="w-full px-6 py-4 bg-white border-2 border-blue-200 rounded-xl text-2xl font-black text-gray-900 focus:outline-none focus:border-blue-500 transition-colors"
                        placeholder="10"
                        min="1"
                    />
                    <span className="absolute right-6 top-1/2 -translate-y-1/2 text-lg font-bold text-gray-500">tCO₂e</span>
                </div>
            </div>

            {/* Impact Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl p-4 border border-green-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <TreePine className="text-green-600" size={20} />
                    </div>
                    <p className="text-2xl font-black text-gray-900">{calculations.trees.toLocaleString()}</p>
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mt-1">Trees Planted (10 years)</p>
                </div>

                <div className="bg-white rounded-xl p-4 border border-orange-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <Car className="text-orange-600" size={20} />
                    </div>
                    <p className="text-2xl font-black text-gray-900">{calculations.car_km.toLocaleString()}</p>
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mt-1">Km Not Driven</p>
                </div>

                <div className="bg-white rounded-xl p-4 border border-yellow-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <Home className="text-yellow-600" size={20} />
                    </div>
                    <p className="text-2xl font-black text-gray-900">{calculations.home_months.toFixed(1)}</p>
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mt-1">Months Home Energy</p>
                </div>

                <div className="bg-white rounded-xl p-4 border border-sky-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <Plane className="text-sky-600" size={20} />
                    </div>
                    <p className="text-2xl font-black text-gray-900">{Math.round(calculations.flights)}</p>
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mt-1">Short-Haul Flights</p>
                </div>

                <div className="bg-white rounded-xl p-4 border border-red-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <Droplet className="text-red-600" size={20} />
                    </div>
                    <p className="text-2xl font-black text-gray-900">{calculations.gallons_gas.toLocaleString()}</p>
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mt-1">Gallons Gasoline</p>
                </div>

                <div className="bg-white rounded-xl p-4 border border-purple-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <Zap className="text-purple-600" size={20} />
                    </div>
                    <p className="text-2xl font-black text-gray-900">{(calculations.smartphones / 1000).toFixed(0)}K</p>
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mt-1">Smartphones Charged</p>
                </div>
            </div>

            {/* Info Box */}
            <div className="mt-6 p-4 bg-blue-100 rounded-xl border border-blue-200">
                <div className="flex items-start gap-3">
                    <Info size={18} className="text-blue-600 mt-0.5 shrink-0" />
                    <div>
                        <p className="text-xs font-bold text-blue-900">How These Calculations Work</p>
                        <p className="text-[10px] text-blue-700 mt-1 leading-relaxed">
                            Based on EPA and IPCC conversion factors: 1 tonne CO₂ = 50 trees for 10 years = 4,000 km driven by average car =
                            1.2 months of home electricity = 0.5 short-haul flight = 110 gallons of gasoline.
                        </p>
                    </div>
                </div>
            </div>

            {/* CTA Button */}
            <button
                onClick={() => {
                    setRetirementAmount(impactTonnes);
                    document.querySelector('button[onClick="handleRetire"]')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="w-full mt-6 px-6 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-black uppercase tracking-wider transition-all flex items-center justify-center gap-2"
            >
                <Leaf size={18} />
                Offset This Amount Now
                <ArrowRight size={18} />
            </button>
        </div>
    );
}
